import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookMarked, Folder, FileText, FileDown, Video, File, Puzzle, ArrowRight } from 'lucide-react';
import { getUsernameFromProfileId, getCoachByPlayerProfile } from '../../../../services/firestoreService';
import { getPinnedLibraryItems } from '../../../../services/libraryService';
import useBlockStyles from '../common/useBlockStyles';

const TYPE_ICONS = {
  folder: Folder,
  pgn: FileText,
  pdf: FileDown,
  video: Video,
  file: File,
  puzzle: Puzzle,
};

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const ContentLibraryRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const [items, setItems] = useState([]);
  const [username, setUsername] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const styles = useBlockStyles(block, theme, STYLE_OPTIONS);

  const maxItems = content.maxItems || 6;
  const title = content.title || 'Content Library';
  const description = content.description || '';
  const buttonText = content.buttonText || 'Browse Library';

  useEffect(() => {
    if (!profileId) return;

    const load = async () => {
      try {
        const [fetchedUsername, coach] = await Promise.all([
          getUsernameFromProfileId(profileId),
          getCoachByPlayerProfile(profileId),
        ]);
        setUsername(fetchedUsername);

        if (coach?.id) {
          const pinned = await getPinnedLibraryItems(coach.id, maxItems);
          setItems(pinned);
        }
      } catch (err) {
        console.error('Error loading library block:', err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [profileId, maxItems]);

  const libraryUrl = username ? `/library/${username}` : '#';

  const accent = styles.accent || '#749555';

  return (
    <section
      className="content-library-block"
      style={{
        background: styles.sectionBg,
        padding: styles.sectionPadding,
      }}
    >
      <div className="clb-container">
        <h2
          className="clb-title"
          style={{
            color: styles.headingColor,
            fontSize: styles.headingSize,
            textAlign: styles.alignment,
            fontFamily: styles.headingFont,
          }}
        >
          {title}
        </h2>
        {description && (
          <p
            className="clb-desc"
            style={{
              color: styles.bodyColor,
              textAlign: styles.alignment,
              fontFamily: styles.bodyFont,
            }}
          >
            {description}
          </p>
        )}

        {isLoading ? (
          <div className="clb-loading">Loading...</div>
        ) : items.length > 0 ? (
          <div className="clb-grid">
            {items.map((item) => {
              const Icon = TYPE_ICONS[item.type] || File;
              return (
                <div key={item.id} className="clb-card" style={{ borderColor: `${accent}33` }}>
                  <div className={`clb-card-icon ${item.type}`}>
                    <Icon size={20} />
                  </div>
                  <div className="clb-card-info">
                    <span className="clb-card-title" style={{ color: styles.headingColor }}>
                      {item.title}
                    </span>
                    {item.description && (
                      <span className="clb-card-desc" style={{ color: styles.bodyColor }}>
                        {item.description}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="clb-empty" style={{ color: styles.bodyColor }}>
            No library items yet. Content will appear here once added.
          </p>
        )}

        <div style={{ textAlign: styles.alignment, marginTop: '24px' }}>
          <Link
            to={libraryUrl}
            className="clb-button"
            style={{
              background: styles.buttonBg,
              color: styles.buttonText,
            }}
          >
            {buttonText} <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      <style>{`
        .content-library-block {
          width: 100%;
        }

        .clb-container {
          max-width: 900px;
          margin: 0 auto;
        }

        .clb-title {
          margin: 0 0 8px;
          font-weight: 700;
        }

        .clb-desc {
          margin: 0 0 24px;
          font-size: 1rem;
          opacity: 0.8;
        }

        .clb-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 12px;
          margin-bottom: 8px;
        }

        .clb-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          border: 1px solid;
          border-radius: 10px;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .clb-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .clb-card-icon {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: white;
        }

        .clb-card-icon.folder { background: #3b82f6; }
        .clb-card-icon.pgn { background: #749555; }
        .clb-card-icon.pdf { background: #ef4444; }
        .clb-card-icon.video { background: #8b5cf6; }
        .clb-card-icon.file { background: #6b7280; }
        .clb-card-icon.puzzle { background: #f59e0b; }

        .clb-card-info {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .clb-card-title {
          font-weight: 600;
          font-size: 0.9rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .clb-card-desc {
          font-size: 0.8rem;
          opacity: 0.7;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .clb-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.95rem;
          text-decoration: none;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .clb-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .clb-loading,
        .clb-empty {
          text-align: center;
          padding: 32px 0;
          opacity: 0.6;
        }

        @media (max-width: 640px) {
          .clb-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

export default ContentLibraryRenderer;
